/**
 * Chart Abstraction Excel Generation Utility
 * 
 * Generates multi-tab Excel workbooks for chart abstraction with:
 * - Color-coded tabs for each measure
 * - Measure title in Row 1
 * - Column headers in Row 2
 * - Question text reference in Row 3
 * - Patient data from Row 4+
 * - Frozen header rows and columns
 */

import ExcelJS from 'exceljs';
import type { EncounterFilterType } from '@/components/gap-management/chart-abstraction/ChartAbstractionExportModal';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Tab colors - cycling through a palette
const TAB_COLORS = [
  'FF4B89DC', // Blue
  'FF26A69A', // Teal
  'FF7E57C2', // Purple
  'FFEF5350', // Red
  'FFFFCA28', // Yellow
  'FF66BB6A', // Green
  'FFFF7043', // Orange
  'FF5C6BC0', // Indigo
];

interface MeasureConfig {
  measureId: string;
  title: string;
  questions: string[];
  isMultiStrata?: boolean;
  /** Collection type of the row being exported (defaults to MIPS CQM) */
  submissionMethod?: string;
}

interface GenerateWorkbookParams {
  practiceId: string;
  practiceName: string;
  measures: MeasureConfig[];
  /** One or more encounter filters — a patient is included if ANY matches */
  encounterFilters: EncounterFilterType[];
  /** Viewing year — required so the export matches the year on screen */
  year?: number;
  /** Active view lane (defaults to GROUP) */
  evaluationType?: 'GROUP' | 'NPI';
  /** Specific provider when in NPI view */
  npi?: string;
}

interface PatientData {
  pid: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  dob: string;
  dos: string;
  npi: string;
  npiName: string;
  stratum?: string;
  // Numerator/denominator flags for filtering
  den?: boolean;
  met?: boolean;
  not?: boolean;
  ex?: boolean;
  exc?: boolean;
  [key: string]: any;
}

/**
 * Generate and download a Chart Abstraction workbook
 */
export async function generateChartAbstractionWorkbook(params: GenerateWorkbookParams): Promise<void> {
  const { practiceId, practiceName, measures, encounterFilters, year, evaluationType, npi } = params;
  
  // Create workbook
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'VBCA MIPS Application';
  workbook.created = new Date();
  
  // Add an instructions sheet first
  const instructionsSheet = workbook.addWorksheet('Instructions', {
    properties: { tabColor: { argb: 'FF808080' } }
  });
  
  addInstructionsSheet(instructionsSheet, practiceName, encounterFilters, measures.length);
  
  // Process each measure
  for (let i = 0; i < measures.length; i++) {
    const measure = measures[i];
    const tabColor = TAB_COLORS[i % TAB_COLORS.length];
    
    try {
      // Fetch patient data for this measure (scoped to the active view lane)
      const patients = await fetchPatientData(practiceId, measure, encounterFilters, year, evaluationType, npi);
      
      // Create worksheet for this measure
      const sheetName = `M${measure.measureId}`;
      const worksheet = workbook.addWorksheet(sheetName, {
        properties: { tabColor: { argb: tabColor } },
        views: [{ state: 'frozen', xSplit: 2, ySplit: 3 }] // Freeze first 2 columns (MRN, Name) and 3 rows (title, headers, questions)
      });
      
      // Add headers and data
      addMeasureWorksheet(worksheet, measure, patients);
      
    } catch (error) {
      console.error(`Error processing measure ${measure.measureId}:`, error);
      
      // Add error sheet instead
      const errorSheet = workbook.addWorksheet(`M${measure.measureId} - Error`, {
        properties: { tabColor: { argb: 'FFFF0000' } }
      });
      errorSheet.getCell('A1').value = `Error loading data for Measure ${measure.measureId}`;
      errorSheet.getCell('A2').value = error instanceof Error ? error.message : 'Unknown error';
    }
  }
  
  // Generate blob and download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  // Create download link
  const exportDate = new Date().toISOString().split('T')[0];
  const filename = `${practiceName} Chart Abstraction List ${exportDate}.xlsx`;
  
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * Add the instructions sheet
 */
function addInstructionsSheet(
  worksheet: ExcelJS.Worksheet, 
  practiceName: string, 
  filters: EncounterFilterType[],
  measureCount: number
): void {
  // Title
  worksheet.getCell('A1').value = 'Chart Abstraction List';
  worksheet.getCell('A1').font = { bold: true, size: 18 };
  
  // Practice info
  worksheet.getCell('A3').value = 'Practice:';
  worksheet.getCell('B3').value = practiceName;
  worksheet.getCell('B3').font = { bold: true };
  
  worksheet.getCell('A4').value = 'Export Date:';
  worksheet.getCell('B4').value = new Date().toLocaleDateString();
  
  worksheet.getCell('A5').value = 'Encounter Filters:';
  worksheet.getCell('B5').value = filters.map(getFilterLabel).join(' + ') || 'None';
  
  worksheet.getCell('A6').value = 'Measures Included:';
  worksheet.getCell('B6').value = measureCount;
  
  // Instructions
  worksheet.getCell('A8').value = 'Instructions';
  worksheet.getCell('A8').font = { bold: true, size: 14 };
  
  const instructions = [
    '1. Each tab contains patients for a specific quality measure.',
    '2. Row 1 displays the measure ID and title.',
    '3. Row 2 contains column headers (MRN, Patient Name, DOB, etc.).',
    '4. Row 3 contains the actual question text for reference.',
    '5. Patient data starts from Row 4.',
    '6. Enter your documentation/abstraction notes in the Question columns.',
    '7. First 2 columns (MRN, Name) and 3 header rows are frozen for easy navigation.',
  ];
  
  instructions.forEach((text, index) => {
    worksheet.getCell(`A${9 + index}`).value = text;
  });
  
  // Column widths
  worksheet.getColumn('A').width = 20;
  worksheet.getColumn('B').width = 40;
}

/**
 * Add a measure worksheet with patient data
 * Layout:
 *   Row 1: Measure title (merged across columns)
 *   Row 2: Column headers
 *   Row 3: Question text reference row
 *   Row 4+: Patient data
 */
function addMeasureWorksheet(
  worksheet: ExcelJS.Worksheet,
  measure: MeasureConfig,
  patients: PatientData[]
): void {
  // Column widths
  const baseColumnWidths = [15, 25, 12, 14, 15, 25, 15]; // MRN, Name, DOB, Encounter, NPI, Provider Name, Stratum
  const questionColumnWidth = 30;
  const totalColumns = 7 + measure.questions.length;
  
  // Set column widths
  for (let i = 1; i <= totalColumns; i++) {
    worksheet.getColumn(i).width = i <= 7 ? baseColumnWidths[i - 1] : questionColumnWidth;
  }
  
  // ROW 1: Measure title
  const titleRow = worksheet.getRow(1);
  titleRow.getCell(1).value = `Measure ${measure.measureId}: ${measure.title}`;
  titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
  titleRow.getCell(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF2E7D32' }, // Dark green
  };
  titleRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
  titleRow.height = 24;
  worksheet.mergeCells(1, 1, 1, totalColumns);
  
  // ROW 2: Column headers
  const headerRow = worksheet.getRow(2);
  const headers = ['MRN', 'Patient Name', 'DOB', 'Encounter Date', 'Provider NPI', 'Provider Name', 'Stratum'];
  measure.questions.forEach((_, index) => {
    headers.push(`Question ${index + 1}`);
  });
  
  headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }; // White text
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF4A90D9' },
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });
  
  // ROW 3: Question text reference row
  const refRow = worksheet.getRow(3);
  // Patient info columns are blank
  for (let i = 1; i <= 7; i++) {
    refRow.getCell(i).value = '';
  }
  // Question columns show the question text
  measure.questions.forEach((question, index) => {
    refRow.getCell(8 + index).value = question;
  });
  
  refRow.font = { italic: true, size: 10 };
  refRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFF5F5F5' },
  };
  refRow.alignment = { vertical: 'top', wrapText: true };
  
  // Calculate row height based on longest question (approx 50 chars per line at width 30)
  const maxQuestionLength = Math.max(...measure.questions.map(q => q.length), 0);
  const estimatedLines = Math.ceil(maxQuestionLength / 40); // ~40 chars per line with wrap
  refRow.height = Math.max(60, estimatedLines * 15); // Min 60, ~15 per line
  
  // ROW 4+: Patient data
  patients.forEach((patient, index) => {
    const row = worksheet.getRow(4 + index);
    
    row.getCell(1).value = patient.pid || '';
    row.getCell(2).value = patient.fullName || `${patient.lastName || ''}, ${patient.firstName || ''}`.trim() || '';
    row.getCell(3).value = patient.dob || '';
    row.getCell(4).value = patient.dos || '';
    row.getCell(5).value = patient.npi || '';
    row.getCell(6).value = patient.npiName || '';
    row.getCell(7).value = patient.stratum || 'N/A';
    
    // Question columns are left empty for user input
    measure.questions.forEach((_, qIndex) => {
      row.getCell(8 + qIndex).value = '';
    });
    
    // Alternate row colors
    if (index % 2 === 0) {
      row.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFAFAFA' },
      };
    }
  });
  
  // Add borders to all data cells (rows 2 through end of data)
  const lastRow = 3 + patients.length;
  
  for (let r = 2; r <= lastRow; r++) {
    for (let c = 1; c <= totalColumns; c++) {
      const cell = worksheet.getCell(r, c);
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFD0D0D0' } },
        left: { style: 'thin', color: { argb: 'FFD0D0D0' } },
        bottom: { style: 'thin', color: { argb: 'FFD0D0D0' } },
        right: { style: 'thin', color: { argb: 'FFD0D0D0' } },
      };
    }
  }
}

/**
 * Does this patient row match ONE encounter filter?
 * Semantics mirror the export modal's count preview exactly:
 *   incomplete  = in denominator with NO numerator outcome recorded
 *                 (not met, not not-met, not exception)
 *   met         = numerator met
 *   not_met     = numerator not met
 *   exclusions  = denominator exclusion OR numerator exception
 */
function matchesFilter(p: PatientData, filter: EncounterFilterType): boolean {
  switch (filter) {
    case 'incomplete':
      return !!p.den && !p.met && !p.not && !p.exc;
    case 'met':
      return !!p.met;
    case 'not_met':
      return !!p.not;
    case 'exclusions':
      return !!p.ex || !!p.exc;
    default:
      return false;
  }
}

/**
 * Fetch patient data from the API and apply the selected encounter filters
 * (a patient is included if ANY selected filter matches).
 * Uses /api/performance/:practiceId/measures/:measureId/patients endpoint
 */
async function fetchPatientData(
  practiceId: string, 
  measure: MeasureConfig,
  filters: EncounterFilterType[],
  year?: number,
  evaluationType?: 'GROUP' | 'NPI',
  npi?: string
): Promise<PatientData[]> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication required');
  }
  
  try {
    // The performance API uses submissionMethod and evaluationType; the year
    // must be threaded so the export matches the viewing year on screen
    const params = new URLSearchParams({
      submissionMethod: measure.submissionMethod || 'MIPS CQM',
      evaluationType: evaluationType || 'GROUP',
    });
    if (year != null) params.set('year', String(year));
    if (evaluationType === 'NPI' && npi) params.set('npi', npi);
    
    const response = await fetch(
      `${API_URL}/api/performance/${practiceId}/measures/${measure.measureId}/patients?${params}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to fetch patient data: ${response.statusText}`);
    }
    
    const patients = await response.json();
    
    return patients.filter((p: PatientData) => filters.some(f => matchesFilter(p, f)));
    
  } catch (error) {
    console.error(`Error fetching patient data for M${measure.measureId}:`, error);
    throw error;
  }
}

/**
 * Get human-readable filter label
 */
function getFilterLabel(filter: EncounterFilterType): string {
  switch (filter) {
    case 'incomplete': return 'Incomplete (needs documentation)';
    case 'met': return 'Numerator Met';
    case 'not_met': return 'Numerator Not Met';
    case 'exclusions': return 'Denominator Exclusions';
    default: return filter;
  }
}

